// frontend/src/components/book/atelier/store/previewLoader.js
//
// CHARGEUR D'APERCUS (le HTML rendu par le serveur pour chaque page).
//
// Avant : a l'ouverture de l'atelier, les 30 pages + les deux couvertures
// etaient demandees d'un coup (6 en parallele) — ~60 requetes avant meme que
// l'utilisateur ait touche a quoi que ce soit, de quoi epuiser a elle seule
// une bonne part des 300 requetes/minute autorisees ("Trop de requetes").
//
// Maintenant : chaque ecran dit CE QU'IL VEUT VOIR (`want`), le chargeur ne
// va chercher que ca, 4 requetes au plus en meme temps, dans l'ordre de
// priorite, et garde tout en cache. Plusieurs sources se partagent le meme
// cache (le livre ouvert, la bande de pages) sans jamais se doubler.
//
// Cles : 'p:<index>' (page interieure), 'c:front', 'c:back'.

export const PREVIEW_CONCURRENCY = 4;
const RETRY_AFTER_FAILURE_MS = 4000;

export const pageKey = (index) => `p:${index}`;
export const COVER_KEYS = { front: 'c:front', back: 'c:back' };

const realTimers = {
  setTimeout: (...args) => setTimeout(...args),
  now: () => Date.now()
};

/**
 * @param {{fetchPage:(index:number)=>Promise<string>, fetchCover:(face:string)=>Promise<string>}} fetchers
 */
export function createPreviewLoader({ fetchPage, fetchCover, concurrency = PREVIEW_CONCURRENCY, timers = realTimers }) {
  const cache = new Map();
  const inflight = new Set();
  const versions = new Map(); // cle -> numero : un resultat perime est ignore
  const failedAt = new Map();
  const sources = new Map(); // id -> { priority, keys }
  const listeners = new Set();
  let active = 0;
  let version = 0;
  let retryTimer = null;

  const emit = () => { version += 1; listeners.forEach((listener) => listener()); };

  function fetchKey(key) {
    if (key === COVER_KEYS.front) return fetchCover('front');
    if (key === COVER_KEYS.back) return fetchCover('back');
    return fetchPage(Number(key.slice(2)));
  }

  // Cle voulue la plus prioritaire qui n'est ni en cache, ni en vol, ni en
  // echec recent. Priorite = (priorite de la source, rang dans sa liste).
  function pickNext() {
    let best = null;
    let bestScore = Infinity;
    sources.forEach((source) => {
      source.keys.forEach((key, rank) => {
        if (cache.has(key) || inflight.has(key)) return;
        const failed = failedAt.get(key);
        if (failed != null && timers.now() - failed < RETRY_AFTER_FAILURE_MS) return;
        const score = source.priority * 100000 + rank;
        if (score < bestScore) { best = key; bestScore = score; }
      });
    });
    return best;
  }

  function pump() {
    while (active < concurrency) {
      const key = pickNext();
      if (key == null) break;
      start(key);
    }
  }

  function start(key) {
    const startedVersion = versions.get(key) || 0;
    inflight.add(key);
    active += 1;
    let promise;
    try { promise = Promise.resolve(fetchKey(key)); } catch (error) { promise = Promise.reject(error); }
    promise
      .then((html) => {
        if ((versions.get(key) || 0) === startedVersion) {
          cache.set(key, html);
          failedAt.delete(key);
          emit();
        }
        // Sinon : invalide pendant le vol, la bonne version sera redemandee.
      })
      .catch(() => {
        failedAt.set(key, timers.now());
        // Un echec ne bloque rien : on retentera apres une courte pause.
        if (retryTimer == null) {
          retryTimer = timers.setTimeout(() => { retryTimer = null; pump(); }, RETRY_AFTER_FAILURE_MS + 50);
        }
        emit();
      })
      .then(() => {
        inflight.delete(key);
        active -= 1;
        // Prevenir aussi la FIN du travail : `isLoading` est lu au rendu, il doit
        // etre rejoue une fois la requete reellement terminee.
        emit();
        pump();
      });
  }

  return {
    /**
     * Declare ce qu'une source veut voir (remplace sa liste precedente). Les
     * cles deja en cache ne coutent rien ; celles qui ne sont plus voulues
     * mais pas encore parties sont simplement abandonnees.
     */
    want(sourceId, keys, priority = 0) {
      sources.set(sourceId, { priority, keys: [...new Set(keys)] });
      pump();
    },
    release(sourceId) { sources.delete(sourceId); },

    /** Pose une valeur deja connue (ex. apercu recu par une autre voie). */
    set(key, html) { cache.set(key, html); failedAt.delete(key); emit(); },

    /** Une page a change : oublie son apercu ; redemande si quelqu'un le veut. */
    invalidate(keys) {
      const list = keys === 'all' ? [...cache.keys(), ...inflight] : keys;
      list.forEach((key) => {
        versions.set(key, (versions.get(key) || 0) + 1);
        cache.delete(key);
        failedAt.delete(key);
      });
      emit();
      pump();
    },

    /**
     * Les numeros de page ont change (deplacement, insertion, suppression) :
     * permute le cache sans reseau. `mapIndex` renvoie le nouvel index, ou null
     * si la page n'existe plus (son apercu est alors oublie).
     */
    remapPages(mapIndex) {
      const entries = [...cache.entries()].filter(([key]) => key.startsWith('p:'));
      entries.forEach(([key]) => cache.delete(key));
      entries.forEach(([key, html]) => {
        const target = mapIndex(Number(key.slice(2)));
        if (target != null && target >= 0) cache.set(pageKey(target), html);
      });
      emit();
    },

    /**
     * Promesse de l'apercu `key` (survol d'une vignette) : sert le cache, sinon
     * le demande EN PRIORITE, dans la meme file (4 au plus en parallele).
     */
    request(key) {
      if (cache.has(key)) return Promise.resolve(cache.get(key));
      return new Promise((resolve, reject) => {
        const sourceId = `request:${key}`;
        const cleanup = () => { listeners.delete(check); sources.delete(sourceId); }; // eslint-disable-line no-use-before-define
        function check() {
          if (cache.has(key)) { cleanup(); resolve(cache.get(key)); }
          else if (failedAt.has(key)) { cleanup(); reject(new Error('apercu indisponible')); }
        }
        listeners.add(check);
        sources.set(sourceId, { priority: -1, keys: [key] });
        pump();
      });
    },

    /** Une page voulue n'a pas pu etre chargee (et n'est pas en cache). */
    hasFailures() {
      let failed = false;
      sources.forEach((source) => source.keys.forEach((key) => { if (failedAt.has(key) && !cache.has(key)) failed = true; }));
      return failed;
    },
    /** "Reessayer" : on oublie les echecs et on relance tout de suite. */
    retryFailed() {
      failedAt.clear();
      emit();
      pump();
    },

    get: (key) => cache.get(key),
    has: (key) => cache.has(key),
    getVersion: () => version,
    snapshotPages() {
      const pages = {};
      cache.forEach((html, key) => { if (key.startsWith('p:')) pages[Number(key.slice(2))] = html; });
      return pages;
    },
    getActive: () => active,
    isLoading: () => active > 0 || pickNext() != null,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); }
  };
}
